// 「底版生成 pipeline」编辑区 — 嵌入套系编辑页 (Detail.tsx)。
//
// 读写套系 config.plate_pipeline 子对象 (snake_case 字段名是前后端契约)。
// bounded 表单 (不做拖拽 DAG), 有序四段: 生成 → 换脸 → 修脸 → 超分。
// 整块留空 (不启用) → 后端走现状缺省 pipeline; 逐字段留空 → 后端平台默认 (三层解析)。
// openspec change: suite-plate-pipeline-config, tasks 3.1-3.3。

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Slider,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DEFAULT_GENERATE_ENGINE,
  DEFAULT_LORA_WEIGHT,
  DEFAULT_RESTORE_VISIBILITY,
  DEFAULT_UPSCALE_MODEL,
  FACESWAP_MODE_OPTIONS,
  GENERATE_ENGINE_OPTIONS,
  RESTORE_MODEL_OPTIONS,
  type PlateFaceSwapMode,
  type PlateGenerateEngine,
  type PlatePipelineConfig,
  type PlateRestoreModel,
} from '@/api/plate-pipeline';
import { updatePackage, type ContentPackage } from '@/api/content';
import { listModels, type LoRAModel } from '@/api/lora';

const { Text } = Typography;

/** 下拉每项渲染: 名称 + 灰色说明小字。 */
function optionWithDesc(opt: { label?: React.ReactNode; desc?: string }) {
  return (
    <div style={{ padding: '2px 0' }}>
      <div>{opt.label}</div>
      {opt.desc && (
        <div style={{ fontSize: 12, color: '#8c8c8c', whiteSpace: 'normal', lineHeight: 1.4 }}>
          {opt.desc}
        </div>
      )}
    </div>
  );
}

interface Props {
  pkg: ContentPackage;
  onSaved: () => void;
}

/** 从 package config 拆出已存 plate_pipeline (可能缺省)。 */
function readPipeline(config: Record<string, unknown> | undefined): PlatePipelineConfig {
  const pp = (config?.plate_pipeline ?? {}) as Record<string, unknown>;
  const gen = (pp.generate ?? {}) as Record<string, unknown>;
  const fs = (pp.faceswap ?? {}) as Record<string, unknown>;
  const rs = (pp.restore ?? {}) as Record<string, unknown>;
  const up = (pp.upscale ?? {}) as Record<string, unknown>;
  return {
    generate: {
      lora_name: typeof gen.lora_name === 'string' ? gen.lora_name : undefined,
      lora_weight: typeof gen.lora_weight === 'number' ? gen.lora_weight : undefined,
      lora_trigger: typeof gen.lora_trigger === 'string' ? gen.lora_trigger : undefined,
      engine: gen.engine as PlateGenerateEngine | undefined,
      steps: typeof gen.steps === 'number' ? gen.steps : undefined,
      guidance: typeof gen.guidance === 'number' ? gen.guidance : undefined,
    },
    faceswap: {
      engine: typeof fs.engine === 'string' ? fs.engine : undefined,
      mode: fs.mode as PlateFaceSwapMode | undefined,
    },
    restore: {
      model: rs.model as PlateRestoreModel | undefined,
      visibility: typeof rs.visibility === 'number' ? rs.visibility : undefined,
    },
    upscale: {
      enabled: typeof up.enabled === 'boolean' ? up.enabled : undefined,
      model: typeof up.model === 'string' ? up.model : undefined,
    },
  };
}

/** package.config 里是否已声明 plate_pipeline (决定初始是否启用自定义)。 */
function hasPipeline(config: Record<string, unknown> | undefined): boolean {
  return !!config?.plate_pipeline;
}

export function PlatePipelineConfigCard({ pkg, onSaved }: Props) {
  const initial = useMemo(() => readPipeline(pkg.config), [pkg.config]);
  const [enabled, setEnabled] = useState<boolean>(hasPipeline(pkg.config));

  // generate
  const [loraName, setLoraName] = useState<string | undefined>(initial.generate?.lora_name);
  const [loraWeight, setLoraWeight] = useState<number | undefined>(initial.generate?.lora_weight);
  const [loraTrigger, setLoraTrigger] = useState<string | undefined>(initial.generate?.lora_trigger);
  const [genEngine, setGenEngine] = useState<PlateGenerateEngine | undefined>(initial.generate?.engine);
  const [steps, setSteps] = useState<number | undefined>(initial.generate?.steps);
  const [guidance, setGuidance] = useState<number | undefined>(initial.generate?.guidance);

  // faceswap
  const [fsEngine, setFsEngine] = useState<string | undefined>(initial.faceswap?.engine);
  const [fsMode, setFsMode] = useState<PlateFaceSwapMode | undefined>(initial.faceswap?.mode);

  // restore
  const [restoreModel, setRestoreModel] = useState<PlateRestoreModel | undefined>(initial.restore?.model);
  const [restoreVisibility, setRestoreVisibility] = useState<number | undefined>(
    initial.restore?.visibility,
  );

  // upscale
  const [upscaleEnabled, setUpscaleEnabled] = useState<boolean>(initial.upscale?.enabled ?? false);
  const [upscaleModel, setUpscaleModel] = useState<string | undefined>(initial.upscale?.model);

  const [saving, setSaving] = useState(false);
  const [loraModels, setLoraModels] = useState<LoRAModel[]>([]);

  // LoRA 快选: 拉本 App 的 ready 模型名, AutoComplete 支持选或手输。
  useEffect(() => {
    if (!pkg.app_id) return;
    listModels(pkg.app_id)
      .then((r) => setLoraModels(r.data ?? []))
      .catch(() => setLoraModels([]));
  }, [pkg.app_id]);

  const loraOptions = useMemo(
    () =>
      loraModels.map((m) => ({
        value: m.name,
        label: `${m.name}${m.trigger_word ? ` · ${m.trigger_word}` : ''} (${m.status})`,
      })),
    [loraModels],
  );

  // 非法/可疑组合软提示 (不强拦保存)。
  const warnings = useMemo(() => {
    const w: string[] = [];
    const noSwap = !fsMode || fsMode === 'none';
    if (restoreModel && restoreModel !== 'none' && noSwap) {
      w.push('修脸 (restore) 配了模型但换脸 mode=none：纯生成底版做人脸修复意义有限，通常应先换脸。');
    }
    if (loraName && !loraTrigger) {
      w.push('配了 LoRA 但未填触发词 (lora_trigger)：多数套系 LoRA 需触发词才生效，请确认。');
    }
    if (loraTrigger && !loraName) {
      w.push('填了触发词但未选 LoRA (lora_name)：触发词不会生效。');
    }
    if (upscaleEnabled && !upscaleModel) {
      // 非阻断: 留空走默认模型
      w.push(`超分已开但未选模型：将走默认 ${DEFAULT_UPSCALE_MODEL}。`);
    }
    return w;
  }, [fsMode, restoreModel, loraName, loraTrigger, upscaleEnabled, upscaleModel]);

  // 成本/耗时提示: 重段越多越慢越贵。
  const heavyStages = useMemo(() => {
    const parts: string[] = [];
    if (fsMode === 'pulid' || fsMode === 'pulid_reactor') parts.push('PuLID 扩散重绘');
    if (fsMode === 'pulid_reactor') parts.push('末端 ReActor 精修');
    if (restoreModel && restoreModel !== 'none') parts.push('修脸');
    if (upscaleEnabled) parts.push('超分');
    return parts;
  }, [fsMode, restoreModel, upscaleEnabled]);

  const onSave = async () => {
    setSaving(true);
    try {
      // 合并进现有 config — 保留 faceswap / template_swap / cover 等其他键, 不覆盖。
      const baseConfig: Record<string, unknown> = { ...(pkg.config ?? {}) };

      if (!enabled) {
        // 关闭自定义 → 删除 plate_pipeline 键, 回落后端缺省 pipeline。
        delete baseConfig.plate_pipeline;
      } else {
        // 只 emit 运营真正设了的字段; undefined ⇒ 省略, 后端逐字段回落平台默认。
        const generate: Record<string, unknown> = {};
        if (loraName) generate.lora_name = loraName;
        if (loraWeight !== undefined) generate.lora_weight = loraWeight;
        if (loraTrigger) generate.lora_trigger = loraTrigger;
        if (genEngine !== undefined) generate.engine = genEngine;
        if (steps !== undefined) generate.steps = steps;
        if (guidance !== undefined) generate.guidance = guidance;

        const faceswap: Record<string, unknown> = {};
        if (fsEngine) faceswap.engine = fsEngine;
        if (fsMode !== undefined) faceswap.mode = fsMode;

        const restore: Record<string, unknown> = {};
        if (restoreModel !== undefined) restore.model = restoreModel;
        if (restoreVisibility !== undefined) restore.visibility = restoreVisibility;

        const upscale: Record<string, unknown> = { enabled: upscaleEnabled };
        if (upscaleModel) upscale.model = upscaleModel;

        const pipeline: Record<string, unknown> = {};
        if (Object.keys(generate).length) pipeline.generate = generate;
        if (Object.keys(faceswap).length) pipeline.faceswap = faceswap;
        if (Object.keys(restore).length) pipeline.restore = restore;
        pipeline.upscale = upscale;

        baseConfig.plate_pipeline = pipeline;
      }

      await updatePackage(pkg.id, { config: baseConfig });
      message.success(enabled ? '底版 pipeline 已保存' : '已关闭自定义 pipeline (回落平台默认)');
      onSaved();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Divider orientation="left">底版生成 pipeline</Divider>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="有序四段: 生成 → 换脸 → 修脸 → 超分。留空则用平台默认 pipeline (现状零回归)。"
        description="关闭下方开关 = 整块走后端缺省链, 老套系不受影响。开启后各字段仍可逐个留空, 后端会逐字段回落平台默认。"
      />
      <Form.Item>
        <Space>
          <Switch checked={enabled} onChange={setEnabled} />
          <Text>{enabled ? '已启用自定义 pipeline' : '未启用 (走平台默认 pipeline)'}</Text>
        </Space>
      </Form.Item>

      {enabled && (
        <Form layout="vertical" style={{ maxWidth: 560 }}>
          {/* ---------- 生成段 ---------- */}
          <Divider orientation="left" plain>
            <Tag color="blue">1</Tag> 生成 (base dev + LoRA)
          </Divider>
          <Form.Item
            label="LoRA 名称 (lora_name)"
            extra="套系风格 LoRA (可选)。可从本 App 已训练模型快选, 也可手输。留空=纯 base dev。"
          >
            <AutoComplete
              allowClear
              placeholder="留空=不挂 LoRA"
              value={loraName}
              options={loraOptions}
              filterOption={(input, opt) =>
                (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={(v) => setLoraName(v || undefined)}
            />
          </Form.Item>
          <Form.Item
            label={`LoRA 权重 (lora_weight): ${loraWeight ?? `默认 ${DEFAULT_LORA_WEIGHT}`}`}
            extra="LoRA 强度, 越高风格越重。推荐 0.7。留空=平台默认。"
          >
            <Slider
              min={0}
              max={1.5}
              step={0.05}
              value={loraWeight ?? DEFAULT_LORA_WEIGHT}
              onChange={setLoraWeight}
              marks={{ 0: '0', 0.7: { label: '0.7 推荐', style: { color: '#52c41a' } }, 1.5: '1.5' }}
            />
          </Form.Item>
          <Form.Item label="LoRA 触发词 (lora_trigger)" extra="挂 LoRA 时通常需要的触发词, 拼进 prompt。">
            <Input
              allowClear
              placeholder="如 hkstyle80s"
              value={loraTrigger}
              onChange={(e) => setLoraTrigger(e.target.value || undefined)}
            />
          </Form.Item>
          <Form.Item label="生成引擎 (engine)" extra={`默认 ${DEFAULT_GENERATE_ENGINE}。留空=平台默认。`}>
            <Select<PlateGenerateEngine>
              placeholder={`平台默认 (${DEFAULT_GENERATE_ENGINE})`}
              allowClear
              value={genEngine}
              options={GENERATE_ENGINE_OPTIONS}
              optionRender={(o) => optionWithDesc(o.data)}
              onChange={(v) => setGenEngine(v)}
            />
          </Form.Item>
          <Space size="large" wrap>
            <Form.Item label="步数 (steps)" extra="留空=平台默认 (~28)">
              <InputNumber
                min={1}
                max={100}
                placeholder="默认"
                value={steps}
                onChange={(v) => setSteps(typeof v === 'number' ? v : undefined)}
              />
            </Form.Item>
            <Form.Item label="引导 (guidance)" extra="留空=平台默认 (~3.0)">
              <InputNumber
                min={0}
                max={20}
                step={0.1}
                placeholder="默认"
                value={guidance}
                onChange={(v) => setGuidance(typeof v === 'number' ? v : undefined)}
              />
            </Form.Item>
          </Space>

          {/* ---------- 换脸段 ---------- */}
          <Divider orientation="left" plain>
            <Tag color="blue">2</Tag> 换脸 (faceswap)
          </Divider>
          <Form.Item
            label="换脸模式 (mode)"
            extra="none=只生成 · reactor=精确换脸(快省) · pulid=神似(扩散重绘,慢) · pulid_reactor=叠加(最像,最重)。留空=平台默认。"
          >
            <Select<PlateFaceSwapMode>
              placeholder="平台默认"
              allowClear
              value={fsMode}
              options={FACESWAP_MODE_OPTIONS}
              optionRender={(o) => optionWithDesc(o.data)}
              onChange={(v) => setFsMode(v)}
            />
          </Form.Item>
          <Form.Item label="换脸引擎 (engine)" extra="通常留空=平台默认 (onething)。">
            <Input
              allowClear
              placeholder="平台默认 (onething)"
              value={fsEngine}
              onChange={(e) => setFsEngine(e.target.value || undefined)}
            />
          </Form.Item>

          {/* ---------- 修脸段 ---------- */}
          <Divider orientation="left" plain>
            <Tag color="blue">3</Tag> 修脸 (restore)
          </Divider>
          <Form.Item
            label="修脸模型 (model)"
            extra="none=不修 · GFPGANv1.4=快但偏磨皮 · codeformer=更自然。留空=平台默认。"
          >
            <Select<PlateRestoreModel>
              placeholder="平台默认"
              allowClear
              value={restoreModel}
              options={RESTORE_MODEL_OPTIONS}
              optionRender={(o) => optionWithDesc(o.data)}
              onChange={(v) => setRestoreModel(v)}
            />
          </Form.Item>
          <Form.Item
            label={`修脸可见度 (visibility): ${restoreVisibility ?? `默认 ${DEFAULT_RESTORE_VISIBILITY}`}`}
            extra="修复强度。越高越光滑, 1.0 过度平滑、塑料感强。推荐 0.65。留空=平台默认。"
          >
            <Slider
              min={0}
              max={1}
              step={0.05}
              value={restoreVisibility ?? DEFAULT_RESTORE_VISIBILITY}
              onChange={setRestoreVisibility}
              marks={{
                0: { label: '0 · 保真', style: { fontSize: 11 } },
                0.65: { label: '0.65 推荐', style: { fontSize: 11, color: '#52c41a' } },
                1: { label: '1 · 塑料感', style: { fontSize: 11, color: '#faad14' } },
              }}
            />
          </Form.Item>

          {/* ---------- 超分段 ---------- */}
          <Divider orientation="left" plain>
            <Tag color="blue">4</Tag> 超分 (upscale)
          </Divider>
          <Form.Item extra="RealESRGAN 4x 超分, 成品更高清。开启显著增加耗时与 GPU 成本。">
            <Space>
              <Switch checked={upscaleEnabled} onChange={setUpscaleEnabled} />
              <Text>{upscaleEnabled ? '已开启超分' : '不超分'}</Text>
            </Space>
          </Form.Item>
          {upscaleEnabled && (
            <Form.Item label="超分模型 (model)" extra={`留空=默认 ${DEFAULT_UPSCALE_MODEL}`}>
              <Input
                allowClear
                placeholder={DEFAULT_UPSCALE_MODEL}
                value={upscaleModel}
                onChange={(e) => setUpscaleModel(e.target.value || undefined)}
              />
            </Form.Item>
          )}

          {/* ---------- 成本/耗时 + 校验提示 ---------- */}
          {heavyStages.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`重段已启用: ${heavyStages.join(' + ')} — 单张底版更重、更慢、更贵。`}
              description="pulid_reactor + 修脸 + 超分 全开是最重组合。量产套系建议按需只开必要段。"
            />
          )}
          {warnings.map((w, i) => (
            <Alert key={i} type="warning" showIcon style={{ marginBottom: 8 }} message={w} />
          ))}
          {warnings.length > 0 && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
              以上为软提示, 不影响保存。
            </Text>
          )}

          <Space>
            <Button type="primary" loading={saving} onClick={onSave}>
              保存底版 pipeline
            </Button>
          </Space>
        </Form>
      )}

      {!enabled && (
        <Space>
          <Button type="primary" loading={saving} onClick={onSave}>
            保存 (关闭自定义)
          </Button>
        </Space>
      )}
    </>
  );
}
